import { Injectable } from '@angular/core';
import { FeatureAggregationService } from '@y42/feature';
import {
  clearPropertyValueOnModel,
  getAvailableLocation,
  GoJsModelProperties,
  GoJsSearchNode,
  IS_SIDEBAR_TYPE_PROP,
  withoutUndoManager,
} from '@y42/gojs';
import { OrchestrationDiagramNodeConfig, OrchestrationGitLink } from '@y42/json-schema';
import { Diagram, GraphLinksModel, Model, Point } from 'gojs';
import isNull from 'lodash/isNull';
import * as uuid from 'uuid';
import { OrchestrationNodeLogsDialogService } from '../../orchestration-node-logs-dialog/orchestration-node-logs-dialog.service';
import {
  isOrchestrationDiagramEditable,
  OrchestrationDerivedNodeDictionary,
  OrchestrationDiagramConfig,
  OrchestrationDiagramModelProperties,
  OrchestrationDiagramUIModel,
  OrchestrationNodeBindings,
  OrchestrationUINode,
} from '../models/orchestration-diagram.model';
import { OrchestrationDiagramHydrationService } from './orchestration-diagram-hydration.service';

@Injectable()
export class OrchestrationDiagramDataService {
  constructor(
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly orchestrationDiagramHydrationService: OrchestrationDiagramHydrationService,
    private readonly orchestrationNodeLogsDialogService: OrchestrationNodeLogsDialogService,
  ) {}

  private _entityInputsMapCache: Record<string, string[]> | null = null;

  setDiagramModel(
    diagram: Diagram,
    config: OrchestrationDiagramConfig,
    dictionary: OrchestrationDerivedNodeDictionary,
    hasDarkMode = false,
  ) {
    const { linkDataArray, nodeDataArray, visualDataArray } = config.model;

    const nodes = nodeDataArray.map((n) => {
      const derivedNode = dictionary[n.path];
      return {
        ...n,
        [OrchestrationNodeBindings.DeletedSource]: !derivedNode,
        [OrchestrationNodeBindings.AllowIncremental]: !!derivedNode?.allowIncremental,
      };
    });

    const model = Model.fromJson(
      JSON.stringify({
        class: 'go.GraphLinksModel',
        linkDataArray,
        nodeDataArray: [...nodes, ...visualDataArray],
      }),
    );

    model.modelData = {
      [OrchestrationDiagramModelProperties.DiagramMode]: config.mode,
      [OrchestrationDiagramModelProperties.DerivedNodesDictionary]: dictionary,
      [GoJsModelProperties.HasDarkMode]: hasDarkMode,
    };

    diagram.model = model;

    diagram.undoManager.isEnabled = isOrchestrationDiagramEditable(config.mode);
    if (isOrchestrationDiagramEditable(config.mode)) {
      diagram.layout.isRealtime = false;
      diagram.layout.isOngoing = false;
      diagram.layout.isInitial = false;
    }
  }

  generateSearchNodes(
    model: OrchestrationDiagramUIModel,
    dictionary: OrchestrationDerivedNodeDictionary,
  ): GoJsSearchNode[] {
    return (model.nodeDataArray || []).map((n: OrchestrationUINode) => {
      const derivedNode = dictionary[n.path];
      if (!derivedNode) {
        return {
          key: n.key,
          entityName: n.path,
          subEntityName: n.path,
        } as GoJsSearchNode;
      }

      const { featureType } = this.featureAggregationService.getServiceDefinitionByPath(n.path)!;

      const searchNode: GoJsSearchNode = {
        key: n.key,
        entityName: derivedNode.entityName,
        entityTypeIcon: derivedNode.typeLogo,
        nodeType: featureType,
        nodeTypeIcon: this.featureAggregationService.getLogoByFeatureType(featureType, 0)!,
        subEntityName: derivedNode.subEntityName,
        subEntityTypeIcon: this.featureAggregationService.getLogoByFeatureType(featureType, 1)!,
      };

      return searchNode;
    });
  }

  parseDiagramModel(modelString: string): OrchestrationDiagramUIModel {
    const json = JSON.parse(modelString) as GraphLinksModel;
    return {
      linkDataArray: json.linkDataArray as OrchestrationGitLink[],
      nodeDataArray: json.nodeDataArray.filter((n) => !n[IS_SIDEBAR_TYPE_PROP]) as OrchestrationUINode[],
      visualDataArray: json.nodeDataArray.filter((n) => n[IS_SIDEBAR_TYPE_PROP]),
    };
  }

  toggleImportTypeOnModel(diagram: Diagram, nodeData: OrchestrationUINode) {
    const node = diagram.findNodeForKey(nodeData.key);
    if (!node) {
      return;
    }
    const updatedNodeConfig: OrchestrationDiagramNodeConfig = { incremental: !nodeData.nodeConfig?.incremental };
    diagram.model.commit((model) => model.set(node.data, OrchestrationNodeBindings.NodeConfig, updatedNodeConfig));
  }

  toggleOnFailureOnModel(diagram: Diagram, nodeData: OrchestrationUINode) {
    const node = diagram.findNodeForKey(nodeData.key);
    if (!node) {
      return;
    }
    const updatedOnFailure = nodeData.onFailure === 'ignore' ? 'stop' : 'ignore';
    diagram.model.commit((model) => model.set(node.data, OrchestrationNodeBindings.OnFailure, updatedOnFailure));
  }

  openJobLogsForNode(nodeData: OrchestrationUINode) {
    const { jobId, path } = nodeData;

    if (!jobId) {
      return;
    }

    this.orchestrationNodeLogsDialogService.showDialog({ jobId, path });
  }

  highlightActiveNodeInDiagram(diagram: Diagram, activeNode: OrchestrationUINode | undefined): void {
    clearPropertyValueOnModel(diagram, OrchestrationNodeBindings.Active, true);

    if (!activeNode) {
      return;
    }
    const node = diagram.findNodeForKey(activeNode.key);

    if (!node) {
      return;
    }

    withoutUndoManager(diagram, () => diagram.model.set(node.data, OrchestrationNodeBindings.Active, true));
  }

  async generateDiagramNodesAndLinks(
    selectedItemPaths: string[],
    diagram: go.Diagram,
    withDependencies = false,
  ): Promise<{ nodeDataArray: OrchestrationUINode[]; linkDataArray: OrchestrationGitLink[] }> {
    const nodesBucket: OrchestrationUINode[] = [];
    const linksBucket: OrchestrationGitLink[] = [];

    this._entityInputsMapCache = null; // clear cache always before using "generateParentNode" method that uses "getEntityInputsMapCached"
    for (const itemPath of selectedItemPaths) {
      if (itemPath) {
        this.generateNode(itemPath, nodesBucket, diagram);

        if (withDependencies) {
          await this.generateParentNode(itemPath, nodesBucket, linksBucket, diagram);
        }
      }
    }

    return { linkDataArray: linksBucket, nodeDataArray: nodesBucket };
  }

  private async getEntityInputsMapCached() {
    if (this._entityInputsMapCache === null) {
      this._entityInputsMapCache = await this.orchestrationDiagramHydrationService.getEntityInputsMap();
    }

    return this._entityInputsMapCache;
  }

  private async generateParentNode(
    currentTableId: string,
    nodesBucket: OrchestrationUINode[],
    linksBucket: OrchestrationGitLink[],
    diagram: go.Diagram,
  ): Promise<void> {
    // fetch table dependencies
    const entityInputsMap = await this.getEntityInputsMapCached();
    const inputTables = entityInputsMap[currentTableId];

    if (!inputTables) {
      return;
    }

    for (const inputTableId of inputTables) {
      // only add a node once to avoid circular dependency
      if (
        !!inputTableId &&
        !this.nodeExistsInDiagram(inputTableId, diagram) &&
        !this.nodeExistsInArray(inputTableId, nodesBucket)
      ) {
        this.generateNode(inputTableId, nodesBucket, diagram);
        await this.generateParentNode(inputTableId, nodesBucket, linksBucket, diagram);
      }
      // link to existing nodes
      this.generateLink(inputTableId, currentTableId, linksBucket, nodesBucket, diagram);
    }
  }

  private generateLink(
    fromEntityId: string,
    toEntityId: string,
    linksBucket: OrchestrationGitLink[],
    nodesBucket: OrchestrationUINode[],
    diagram: go.Diagram,
  ): void {
    const from = this.getExistingNodeKey(fromEntityId, nodesBucket, diagram);
    const to = this.getExistingNodeKey(toEntityId, nodesBucket, diagram);

    if (isNull(from) || isNull(to)) {
      return;
    }

    const existingLinksInDiagram = diagram.findLinksByExample({ from, to });
    const existingLinksInArray = linksBucket.find((l) => l.from === from && l.to === to);
    if (existingLinksInDiagram.count === 0 && !existingLinksInArray) {
      linksBucket.push({ from, to } as OrchestrationGitLink);
    }
  }

  private getExistingNodeKey(entryId: string, nodesBucket: OrchestrationUINode[], diagram: go.Diagram): string | null {
    const inDiagram = diagram.findNodesByExample({
      path: (path: string) => path === entryId,
    });
    if (inDiagram.first()) {
      return inDiagram.first()?.data.key || null;
    }

    const inBucket = nodesBucket.find((n) => n.path === entryId);
    if (inBucket) {
      return inBucket.key;
    }

    return null;
  }

  private generateNode(itemId: string, nodesBucket: OrchestrationUINode[], diagram: go.Diagram): void {
    const location = getAvailableLocation(diagram);

    if (!itemId) {
      return;
    }

    const derivedNode = this.orchestrationDiagramHydrationService.orchestrationDerivedNodesDictionary?.[itemId];

    const newNode: OrchestrationUINode = {
      key: uuid.v4(),
      entityType: derivedNode?.nodeType || '',
      path: itemId,
      location: Point.stringify(location),
      onFailure: 'stop',
      nodeConfig: {
        incremental: !!derivedNode?.allowIncremental,
      },
      [OrchestrationNodeBindings.DeletedSource]: !derivedNode,
      [OrchestrationNodeBindings.AllowIncremental]: !!derivedNode?.allowIncremental,
    };

    // do not allow duplicates for dependencies
    if (!this.nodeExistsInDiagram(itemId, diagram) && !this.nodeExistsInArray(itemId, nodesBucket)) {
      nodesBucket.push(newNode);
    }
  }

  private nodeExistsInDiagram(itemId: string, diagram: go.Diagram): boolean {
    const nodes = diagram.findNodesByExample({
      path: (path: string) => path === itemId,
    });
    return !!nodes && nodes.count > 0;
  }

  private nodeExistsInArray(itemId: string, nodesBucket: OrchestrationUINode[]): boolean {
    const nodesFound = nodesBucket.find((n) => n.path === itemId);
    return !!nodesFound;
  }
}
